/**
 * حالة الجلسة — مصدر واحد لـ«مين اللي داخل» في كل الواجهة.
 *
 * الجلسة كوكي HttpOnly، يعني الواجهة **مش شايفة التوكن**. الطريقة الوحيدة
 * لمعرفة إن فيه جلسة هي `GET /auth/me` عند الإقلاع.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchMe, login as loginRequest, logout as logoutRequest } from "@/api/auth";
import { ApiError, OfflineError, UNAUTHORIZED_EVENT } from "@/api/client";
import type { MePayload } from "@/api/types";

export type SessionStatus = "loading" | "authed" | "guest" | "offline";

type SessionValue = {
  status: SessionStatus;
  me: MePayload | null;
  /** رسالة بتعيش لحد شاشة الدخول — زي «خرجت من الجهاز ده بس السيستم ما أكدش» */
  notice: string | null;
  clearNotice: () => void;
  signIn: (identifier: string, password: string, remember: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
};

const SessionCtx = createContext<SessionValue | null>(null);

/**
 * جلسة عرض للتطوير المحلي بس — عشان نقدر نشوف الشاشات قبل ما الباك إند يقوم.
 * ⛔ محروسة بـ`import.meta.env.DEV`: مستحيل تشتغل في بيلد الإنتاج.
 *    التشغيل: `VITE_MOCK_SESSION=1 npm run dev`
 */
const MOCK_SESSION_ON = import.meta.env.DEV && import.meta.env.VITE_MOCK_SESSION === "1";

const MOCK_ME: MePayload = {
  user: {
    id: "mock-1",
    full_name_ar: "أ. مشرفة الدعم أ",
    username: "support_a",
    role: "member",
    department: "student_support",
    department_name_ar: "دعم الطلاب",
    role_title_ar: "دعم الطلاب (المبيعات)",
  },
  permissions: [],
  department: "student_support",
  unread_notifications: 2,
  open_tasks: 7,
  overdue_tasks: 2,
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [me, setMe] = useState<MePayload | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (MOCK_SESSION_ON) {
      console.warn("[session] جلسة عرض محلية شغّالة — مفيش مصادقة حقيقية (VITE_MOCK_SESSION=1)");
      setMe(MOCK_ME);
      setStatus("authed");
      return;
    }
    try {
      const payload = await fetchMe();
      setMe(payload);
      setStatus("authed");
    } catch (err) {
      setMe(null);
      // ⚠️ فرق مهم: مفيش اتصال ≠ مش مسجّل دخول. لو خلطناهم بنطرد الموظف
      //    من السيستم كل ما النت يقطع لحظة — وكمان بنوديه لشاشة دخول
      //    هيفشل فيها برضه لأن السيستم نفسه واقع.
      const serverDown = err instanceof ApiError && err.status >= 500;
      if (err instanceof OfflineError || serverDown) {
        setStatus("offline");
      } else {
        setStatus("guest");
      }
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // انتهاء الجلسة من أي طلب في أي شاشة ⇒ رجوع لشاشة الدخول
  useEffect(() => {
    const onUnauthorized = () => {
      setMe(null);
      setStatus("guest");
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const signIn = useCallback(
    async (identifier: string, password: string, remember: boolean) => {
      if (MOCK_SESSION_ON) {
        setMe(MOCK_ME);
        setStatus("authed");
        return;
      }
      await loginRequest(identifier, password, remember);
      // بننده /auth/me بعد الدخول عشان الصلاحيات والعدّادات تيجي من مصدر واحد
      const payload = await fetchMe();
      setMe(payload);
      setNotice(null);
      setStatus("authed");
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
      setNotice(null);
    } catch {
      // اللوج بيصرخ جوّه `api/auth.ts`. هنا بنقول للموظف الحقيقة بلغته:
      // ⚠️ ممنوع الخروج يفشل صامت — من غير السطر ده كان بيدوس «خروج»،
      //    الشاشة تهتز، ويرجع مكانه بجلسة شغّالة وبلا أي كلمة.
      setNotice(
        "خرجت من الجهاز ده، بس السيستم ما أكدش الخروج — لو الجهاز مش بتاعك بلّغ إدارة الأكاديمية",
      );
    } finally {
      // الخروج المحلي بيتم مهما حصل
      setMe(null);
      setStatus("guest");
    }
  }, []);

  const clearNotice = useCallback(() => setNotice(null), []);

  const value = useMemo<SessionValue>(
    () => ({ status, me, notice, clearNotice, signIn, signOut, reload }),
    [status, me, notice, clearNotice, signIn, signOut, reload],
  );

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionCtx);
  if (!ctx) throw new Error("useSession لازم يتنده جوّه SessionProvider");
  return ctx;
}
